import React from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { useCart } from "../lib/cart";
import { money } from "../lib/api";
import { resolveUnitPrice } from "../lib/pricing";
import CouponField from "../components/storefront/CouponField";
import { PaymentMethods, PolicyLinksRow, PaymentOptionsNote } from "../components/storefront/TrustBadges";
import { useSeo } from "../lib/seo";
import { Plus, Minus, Trash2 } from "lucide-react";

export default function Cart() {
  const { items, updateQty, remove, subtotal, setOpen, coupon, discount, total } = useCart();
  useSeo({ title: "Cart | Lecis Auto Glass", description: "Your shopping cart at Lecis Auto Glass.", canonicalPath: "/cart", robots: "noindex,follow" });

  return (
    <StorefrontLayout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16" data-testid="cart-page">
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-medium mb-12">Cart</h1>

        {items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-md">
            <p className="text-zinc-500 mb-6">Your cart is empty.</p>
            <Link to="/shop" className="inline-block bg-red-600 hover:bg-red-700 px-6 h-11 leading-[2.75rem] text-white rounded-sm">Shop now</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              {items.map((it) => {
                const unit = resolveUnitPrice(it.price, it.price_tiers, it.quantity);
                const tiered = unit < Number(it.price);
                return (
                <div key={it.product_id} className="flex gap-4 p-4 bg-[#0d0d0d] border border-white/5 rounded-md">
                  {it.image && <img src={it.image} alt="" className="w-28 h-28 object-cover rounded" />}
                  <div className="flex-1">
                    <Link to={`/product/${it.slug}`} className="font-medium hover:text-red-500">{it.name}</Link>
                    <div className="text-xs text-zinc-500 font-mono mt-1">{it.sku}</div>
                    {tiered && <div className="text-[11px] text-emerald-500 font-mono mt-1">Volume price {money(unit)} ea</div>}
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-7 h-7 border border-white/10 rounded flex items-center justify-center"><Minus size={12} /></button>
                      <span className="text-sm w-6 text-center font-mono">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-7 h-7 border border-white/10 rounded flex items-center justify-center"><Plus size={12} /></button>
                      <button
                        data-testid={`cart-page-remove-${it.sku}`}
                        onClick={() => remove(it.product_id)}
                        aria-label={`Remove ${it.name} from cart`}
                        title="Remove item"
                        className="ml-auto inline-flex items-center gap-1 h-7 px-2 rounded border border-white/10 text-xs text-zinc-300 hover:text-white hover:border-red-500/60 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 size={13} aria-hidden="true" /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="font-mono">{money(unit * it.quantity)}</div>
                </div>
              );})}
            </div>

            <div className="bg-[#0d0d0d] border border-white/5 p-6 rounded-md h-fit">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">Order summary</div>
              <div className="mb-4"><CouponField /></div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-zinc-400">Subtotal</span>
                <span className="font-mono" data-testid="cart-page-subtotal">{money(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between text-sm mb-2 text-emerald-400" data-testid="cart-page-discount-row">
                  <span>Discount {coupon?.code ? `(${coupon.code})` : ""}</span>
                  <span className="font-mono">−{money(discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm mb-6 text-zinc-500">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between text-base mb-6 border-t border-white/10 pt-3">
                  <span className="font-medium">Total</span>
                  <span className="font-mono" data-testid="cart-page-total">{money(total)}</span>
                </div>
              )}
              <button
                onClick={() => setOpen(true)}
                data-testid="cart-page-checkout-btn"
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium tracking-wide rounded-sm"
              >
                Proceed to checkout
              </button>
              <div className="mt-4 space-y-3">
                <PaymentMethods />
                <PaymentOptionsNote />
                <PolicyLinksRow />
              </div>
            </div>
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
